// ═══════════════════════════════════════════════════════════════
// FILE: src/modules/leadgen/lobby/checkin/checkinShared.js
// FILE VERSION: CHECKINSHARED-v1.7.39.62.01
// CHARLIE VERSION: v1.7.39
// CREATED: 2026-07-27
//
// Constants and pure functions shared by every check-in file.
//
// WHY THIS EXISTS:
//   CheckInFlow.jsx grew to ~900 lines and was rewritten six
//   times in one session for changes averaging twenty lines
//   each. Every notes tweak, every gate adjustment, every
//   dropdown reorder meant regenerating the whole file and
//   re-verifying nine hundred lines of unrelated code.
//
//   Splitting it means a left-panel change touches a 250-line
//   file instead of a 900-line one.
//
// NO REACT IN HERE. No hooks, no JSX, no side effects. Anything
// in this file can be imported anywhere without dragging a
// component tree along with it.
// ═══════════════════════════════════════════════════════════════

export const CRIMSON     = 'var(--gov-brand-red)';
export const SLATE       = 'var(--gov-text-primary)';
export const BORDER      = 'var(--gov-slate-200)';
export const BORDER_SOFT = 'var(--gov-slate-100)';
export const TEXT_MUTED  = 'var(--gov-slate-500)';
export const TEXT_FAINT  = 'var(--gov-slate-400)';
export const GREEN       = 'var(--gov-good)';
export const GREEN_BG    = '#f0fdf4';
export const GREEN_BORDER= '#bbf7d0';
export const AMBER       = 'var(--gov-warning)';

export const WELL_REPD_PARTNER_ID = '6ad8ac03-049f-462f-83fc-8b2bbc7c5bdd';

export const PARTNER_EMAIL_MAP = {
  'Phoenix Oil Tools':              'Phoenix@wellrepd.com',
  'Precise Downhole Solutions':     'Precise@wellrepd.com',
  'Pipe Market':                    'PipeMarket@wellrepd.com',
  'FoxSet':                         'FoxSet@wellrepd.com',
  'Fox Underground':                'FoxUnderground@wellrepd.com',
  'Maverick Downhole Technologies': 'Maverick@wellrepd.com',
  'CT Evolution':                   'CTEvolution@wellrepd.com',
  'Wildcat Oil Tools':              'Wildcat@wellrepd.com',
  'Enventure':                      'Enventure@wellrepd.com',
  'OilRecon USA':                   'oilrecon@wellrepd.com',
  'Silver Fox Completions':         'silverfox@wellrepd.com',
  // v1.7.39.80 — PENCILED IN, mailboxes don't exist yet (Shane, 2026-07-29).
  // partners.email deliberately left NULL for these two -- do not set it
  // until the real mailbox exists, or the Connect flow in
  // EmailConnections.jsx will attempt a real OAuth login against an
  // address that isn't there.
  'Sasquatch Liners':               'sasquatch@wellrepd.com',
  'WM Oilfield Services':           'wmoilfield@wellrepd.com',
  'GenServ Maintenance':            'Hello@genserv.co',
  'Grand Slam Caps':                'Sales@grandslamcaps.com',
  'Well Repd Consulting':           'shane.deason@wellrepd.com',
};

// Reverse lookup. sequences.persona holds the partner's send-from
// address rather than a partner_id, so this is how a sequence
// gets grouped under a readable partner name.
export const EMAIL_TO_PARTNER = Object.fromEntries(
  Object.entries(PARTNER_EMAIL_MAP).map(([name, email]) => [email.toLowerCase(), name])
);

export const GATEKEEPER_FIELDS = [
  { key: 'first_name',   label: 'First name' },
  { key: 'last_name',    label: 'Last name' },
  { key: 'email',        label: 'Email' },
  { key: 'job_title',    label: 'Job title' },
  { key: 'companyName',  label: 'Company name', derived: true },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
];

export const TAG_OPTIONS = [
  { key: 'decision_maker', label: 'Decision Maker' },
  { key: 'gatekeeper',     label: 'Gatekeeper' },
  { key: 'home_owner',     label: 'Home Owner' },
  { key: 'bad_data',       label: 'Bad Data' },
  { key: 'do_not_contact', label: 'Do Not Contact' },
];

export const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com','yahoo.com','outlook.com','hotmail.com','aol.com',
  'icloud.com','me.com','msn.com','live.com','protonmail.com','proton.me',
]);

export const HUMAN_TOUCH_TYPES = [
  { key: 'call',             label: 'Phone Call',       icon: '📞' },
  { key: 'text',             label: 'Text',             icon: '💬' },
  { key: 'voicemail',        label: 'Voicemail Drop',   icon: '📱' },
  { key: 'linkedin_connect', label: 'LinkedIn Connect', icon: '🤝' },
  { key: 'linkedin_message', label: 'LinkedIn Message', icon: '💼' },
  { key: 'email',            label: 'Personal Email',   icon: '✉️' },
  // v1.7.39.74.03 — gift_cookie/gift_coffee removed. Gifting is event-triggered
  // off a logged meeting now, not a check-in-time selectable touch. See
  // SERVICE_TIERS' comment above for the full reasoning.
];

// 2026-07-27: real task_type_enum only has email/call/text/linkedin/
// todo/meeting/voicemail/gift (voicemail and gift added this session
// via migration). Human Touches has 8 keys, the enum has fewer real
// buckets — linkedin_connect and linkedin_message both collapse to
// 'linkedin', gift_cookie and gift_coffee both collapse to 'gift'.
// The specific flavor (which LinkedIn action, which gift) still shows
// up in the task title, it's just not a distinct enum value.
export const HUMAN_TOUCH_TASK_TYPE = {
  call: 'call',
  text: 'text',
  voicemail: 'voicemail',
  linkedin_connect: 'linkedin',
  linkedin_message: 'linkedin',
  gift_cookie: 'gift',
  gift_coffee: 'gift',
  email: 'email',
};

// 2026-07-27: three named service tiers, Shane's call, Audi-family
// naming ("we're an Audi family"). Premium = basic (Hello + Sequence,
// no manual touches). Premium Plus = nurture (adds one light manual
// touch). Prestige = full court press (call, text, gift, personal
// email, all pre-scheduled). A tier button is a CONVENIENCE that sets
// humanTouches + dripEnabled to sensible defaults, it never locks the
// screen — every individual touch stays checkable/uncheckable after
// picking a tier, for the genuine custom-order case.
export const SERVICE_TIERS = [
  {
    key: 'premium',
    label: 'Premium',
    // v1.7.39.74.03 — real redesign, not a tweak. Shane's Audi analogy: "premium
    // still has leather seats," the base tier should not be empty. Premium is now
    // a real floor -- everyone who gets checked in gets genuinely reached, not
    // just dripped at. Gifting removed entirely (see rationale below).
    description: 'The floor: LinkedIn connect, LinkedIn message, hello, and the drip campaign.',
    humanTouches: ['linkedin_connect', 'linkedin_message'],
    dripEnabled: true,
  },
  {
    key: 'premium_plus',
    label: 'Premium Plus',
    description: 'Everything in Premium, plus a hand-written personal follow-up email.',
    humanTouches: ['linkedin_connect', 'linkedin_message', 'email'],
    dripEnabled: true,
  },
  {
    key: 'prestige',
    label: 'Prestige',
    // Call/Text should visually grey out when the contact has no phone on file --
    // real Ledger item logged 2026-07-28, not built yet. Voicemail intentionally
    // excluded -- the voicemail-drop feature itself does not exist yet (Storage >
    // Recordings is a known stub), do not offer a touch type that cannot fire.
    description: 'Everything in Premium Plus, plus a call and a text (if we have a number).',
    humanTouches: ['linkedin_connect', 'linkedin_message', 'email', 'call', 'text'],
    dripEnabled: true,
  },
  // v1.7.39.74.03 — gift_cookie/gift_coffee removed from every tier. Shane's real
  // point: gifting is not a tier CHOICE, it is EVENT-TRIGGERED off a logged
  // meeting (in-person -> cookies, first virtual meeting -> digital gift card,
  // per the gift-program Ledger item). At check-in time, before any meeting has
  // happened, there is nothing to gift yet and no honest way to pre-select it.
  // Gifting now lives entirely outside this tier picker.
];

// WO-0492 Phase 2 (2026-09-15) -- real five-gate Lobby Airlock model,
// A51 direct work order. Replaces the old 3-step Contact&Partner/
// Hello/Nurture wizard. IDENTIFY and CONTACTABILITY are genuinely new
// gates (previously folded silently into step 0's gating logic).
// RECONCILE reuses StepRouting's dupe-detection + partner-routing
// content (contact-type toggle moved out to IDENTIFY). INTAKE renders
// the existing StepCompose + StepNurture content together as one gate
// (Hello Email and Nurture&Send are no longer separate required
// steps -- see CheckInFlow.jsx). DESTINATION is entirely new, wired
// to POST /api/crm?resource=contacts&action=lobby-destination.
export const STEPS = [
  { key: 'identify', label: 'Identify' },
  { key: 'reconcile', label: 'Reconcile' },
  { key: 'contactability', label: 'Contactability' },
  { key: 'intake', label: 'Intake' },
  { key: 'destination', label: 'Destination' },
];

// WO-0492 Phase 2 -- the real, live Destination backend contract
// (Maverick, PR #476, commit c30e415). Kept here, not duplicated,
// since StepDestination.jsx and CheckInFlow.jsx both need the exact
// same reason enums the API itself validates against.
export const PARK_REASONS = [
  { key: 'need_more_research', label: 'Need more research' },
  { key: 'not_ready', label: 'Not ready' },
  { key: 'seasonal_timing', label: 'Seasonal timing' },
  { key: 'wait_for_retainer', label: 'Wait for retainer' },
  { key: 'other', label: 'Other' },
];
export const REMOVE_REASONS = [
  { key: 'wrong_industry', label: 'Wrong industry' },
  { key: 'wrong_role', label: 'Wrong role' },
  { key: 'duplicate', label: 'Duplicate' },
  { key: 'bad_data', label: 'Bad data' },
  { key: 'personal_contact', label: 'Personal contact' },
  { key: 'passed_to_human', label: 'Passed to human' },
  { key: 'kc_baseball', label: 'KC Baseball' },
  { key: 'other', label: 'Other' },
];

export const DAYS  = ['mon', 'tue', 'wed', 'thu', 'fri'];
export const TIMES = ['am', 'pm'];

/**
 * CHARLIE picks the outreach slot, not the human.
 *
 * Rule: spread deliberately, do not pack.
 *
 * Naive "first free day" sends every contact sorted on a Monday
 * out on that same Monday. Fine for one record, useless for a
 * batch — and 3,297 contacts are waiting in Sorting. Spreading
 * has to be the default behavior rather than a bulk-only special
 * case, so the single and bulk paths cannot diverge.
 *
 *   1. Slots this contact already holds are hard-excluded. One
 *      email per contact per day, always.
 *   2. Remaining slots rank by how loaded that day+time already
 *      is book-wide, lightest first.
 *   3. Ties break on a hash of the contact id, so a batch fans
 *      out instead of stacking on whichever slot sorts first.
 *
 * @param {string} contactId
 * @param {Array}  takenByContact  [{ slot_day, slot_time }]
 * @param {Object} loadByKey       { 'mon-am': 42, ... }
 */
export function pickAutoSlot(contactId, takenByContact, loadByKey) {
  const taken = new Set(
    (takenByContact || [])
      .filter((s) => s.slot_day && s.slot_time)
      .map((s) => `${s.slot_day}-${s.slot_time}`)
  );

  const candidates = [];
  for (const day of DAYS) {
    for (const time of TIMES) {
      const key = `${day}-${time}`;
      if (taken.has(key)) continue;
      candidates.push({ day, time, key, load: loadByKey?.[key] ?? 0 });
    }
  }

  // Every slot already used. Rare, but a contact can legitimately
  // sit with ten partners. Fall back to the lightest slot and
  // accept the same-day overlap rather than refusing to sort them.
  if (candidates.length === 0) {
    let lightest = { day: 'mon', time: 'am', load: Infinity };
    for (const day of DAYS) {
      for (const time of TIMES) {
        const load = loadByKey?.[`${day}-${time}`] ?? 0;
        if (load < lightest.load) lightest = { day, time, load };
      }
    }
    return { slotDay: lightest.day, slotTime: lightest.time, slotStart: 'this' };
  }

  let hash = 0;
  for (let i = 0; i < String(contactId).length; i++) {
    hash = (hash * 31 + String(contactId).charCodeAt(i)) >>> 0;
  }

  candidates.sort((a, b) => {
    if (a.load !== b.load) return a.load - b.load;
    const ai = DAYS.indexOf(a.day) * 2 + TIMES.indexOf(a.time);
    const bi = DAYS.indexOf(b.day) * 2 + TIMES.indexOf(b.time);
    return ((ai + hash) % 10) - ((bi + hash) % 10);
  });

  const pick = candidates[0];
  return { slotDay: pick.day, slotTime: pick.time, slotStart: 'this' };
}

/**
 * Plain text template body → HTML for the rich text editor.
 * Content that is already HTML passes through untouched.
 */
export function plainToHtml(text) {
  if (!text) return '';
  if (text.trim().startsWith('<')) return text;
  return text
    .split(/\n\n+/)
    .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Any name plus any way to reach them.
 *
 * Wildcard imports arrive lopsided in both directions. Loran has
 * a first name and an address. Brown has a surname, an address, a
 * phone, a title and a discipline. Both are real people who can
 * be contacted, and a rule naming a specific field will always
 * refuse one of them.
 *
 * The greeting is solved at send time: send-hello.js falls back
 * first_name → last_name → "there", so a one-name contact never
 * produces "Hey ,".
 */
export function computeAdmittable(contact) {
  const hasFirst = !!(contact?.first_name && String(contact.first_name).trim());
  const hasLast  = !!(contact?.last_name && String(contact.last_name).trim());
  const hasEmail = !!(contact?.email && String(contact.email).trim());
  const hasPhone = !!((contact?.mobile_phone && String(contact.mobile_phone).trim()) || (contact?.work_phone && String(contact.work_phone).trim()));
  return (hasFirst || hasLast) && (hasEmail || hasPhone);
}

/** Shared style objects, so panels do not each redefine them. */
export const fieldLabelStyle = { fontSize: 11, fontWeight: 700, color: TEXT_FAINT, letterSpacing: 0.5, marginBottom: 4, display: 'block' };
export const dropStyle       = { width: '100%', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
export const rowStyle        = { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${BORDER_SOFT}`, fontSize: 13 };
export const keyStyle        = { color: TEXT_FAINT, fontWeight: 700, fontSize: 11, letterSpacing: 0.5 };
export const cardStyle       = { background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: 18, marginBottom: 16 };
